import { useRef } from 'react'
import { getMediaUrl } from './ProjectMedia'
import { usePausePlayerOffscreen } from '../lib/videoPolicy'
import { PROJECT_TOPIC_LABEL_CLASS } from '../lib/projectTopicLabel'

const getYoutubeEmbedUrl = (url) => {
  if (!url) return null

  const shortsMatch = url.match(/youtube\.com\/shorts\/([^/?&]+)/)
  if (shortsMatch) return `https://www.youtube.com/embed/${shortsMatch[1]}`

  const watchMatch = url.match(/[?&]v=([^/?&]+)/)
  if (watchMatch) return `https://www.youtube.com/embed/${watchMatch[1]}`

  const youtuBeMatch = url.match(/youtu\.be\/([^/?&]+)/)
  if (youtuBeMatch) return `https://www.youtube.com/embed/${youtuBeMatch[1]}`

  return null
}

const getShortSizeClass = (variant) => {
  if (variant === 'hero') {
    return 'mx-auto aspect-[9/16] w-full max-w-[min(100%,30rem)] sm:max-w-[36rem] md:max-w-[42rem]'
  }

  return 'mx-auto aspect-[9/16] w-full max-w-[27rem]'
}

export const MediaPlaceholder = ({
  label,
  aspectClassName = 'aspect-video',
  isShort = false,
  variant,
}) => {
  const resolvedAspect = isShort
    ? getShortSizeClass(variant)
    : aspectClassName

  return (
    <figure className="space-y-3">
      <div
        className={`site-frame site-media-frame flex ${resolvedAspect} w-full items-center justify-center bg-secondary px-4`}
      >
        <div className="text-center">
          <p className="site-label">placeholder video</p>
          <p className="site-muted mt-2 normal-case leading-relaxed">{label}</p>
        </div>
      </div>
    </figure>
  )
}

export const CaseStudyMedia = ({ item, title = 'Case study media' }) => {
  const videoRef = useRef(null)
  const videoUrl = getMediaUrl(item.video)
  const posterUrl = item.poster ? getMediaUrl(item.poster) : undefined

  usePausePlayerOffscreen(videoRef, () => videoRef.current?.pause(), Boolean(videoUrl))

  if (item.youtube) {
    const embedUrl = getYoutubeEmbedUrl(item.youtube)
    const isShort = item.youtube.includes('/shorts/')

    if (!embedUrl) return null

    return (
      <figure className="space-y-3">
        {item.label ? (
          <p className={PROJECT_TOPIC_LABEL_CLASS}>{item.label.toLowerCase()}</p>
        ) : null}
        <div
          className={[
            'site-frame site-media-frame overflow-hidden',
            isShort ? 'mx-auto aspect-[9/16] max-w-[18rem]' : 'aspect-video w-full',
          ].join(' ')}
        >
          <iframe
            src={embedUrl}
            title={item.title || title}
            className="h-full w-full"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
            allowFullScreen
          />
        </div>
        {item.title ? (
          <figcaption className="site-muted normal-case leading-relaxed">{item.title}</figcaption>
        ) : null}
      </figure>
    )
  }

  if (item.placeholder) {
    return (
      <MediaPlaceholder label={item.label} isShort={item.isShort} variant={item.variant} />
    )
  }

  if (!videoUrl) return null

  const isShort = Boolean(item.isShort)

  return (
    <figure className="space-y-3">
      {item.label ? (
        <p className={PROJECT_TOPIC_LABEL_CLASS}>{item.label.toLowerCase()}</p>
      ) : null}
      <div
        className={[
          'site-frame site-media-frame overflow-hidden bg-secondary',
          isShort ? getShortSizeClass(item.variant) : 'aspect-video w-full',
        ].join(' ')}
      >
        <video
          key={videoUrl}
          ref={videoRef}
          className="h-full w-full object-contain"
          controls
          playsInline
          preload="metadata"
          poster={posterUrl}
          aria-label={item.label || title}
        >
          <source src={videoUrl} type="video/mp4" />
        </video>
      </div>
    </figure>
  )
}

export default CaseStudyMedia
